/**
 * Post-checkout confirmation snapshot in localStorage (shared across HYP popup).
 * TTL 24h. Never treat this as a durable access grant.
 */
const STORAGE_KEY = "hr_public_booking_confirmation_v1";
const TTL_MS = 24 * 60 * 60 * 1000;

function readStore() {
  if (typeof window === "undefined") return { byOrderId: {}, byOrderRef: {} };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { byOrderId: {}, byOrderRef: {} };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { byOrderId: {}, byOrderRef: {} };
    return {
      byOrderId: parsed.byOrderId && typeof parsed.byOrderId === "object" ? parsed.byOrderId : {},
      byOrderRef: parsed.byOrderRef && typeof parsed.byOrderRef === "object" ? parsed.byOrderRef : {},
    };
  } catch {
    return { byOrderId: {}, byOrderRef: {} };
  }
}

function writeStore(store) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* quota / private mode */
  }
}

function isFresh(entry) {
  if (!entry || typeof entry !== "object") return false;
  const exp = Number(entry.expiresAt);
  return Number.isFinite(exp) && exp > Date.now();
}

export function persistPublicBookingConfirmationSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return;
  const orderId = String(snapshot.technicalOrderId || snapshot.orderId || "").trim();
  const orderRef = String(snapshot.orderRef || "").trim();
  if (!orderId && !orderRef) return;
  const store = readStore();
  const entry = {
    ...snapshot,
    expiresAt: Date.now() + TTL_MS,
    savedAt: Date.now(),
  };
  if (orderId) store.byOrderId[orderId] = entry;
  if (orderRef) store.byOrderRef[orderRef.toUpperCase()] = entry;
  writeStore(store);
}

export function readPublicBookingConfirmationSnapshot({ orderId, orderRef } = {}) {
  const store = readStore();
  const id = String(orderId || "").trim();
  const ref = String(orderRef || "").trim().toUpperCase();
  const byId = id ? store.byOrderId[id] : null;
  if (isFresh(byId)) return byId;
  const byRef = ref ? store.byOrderRef[ref] : null;
  if (isFresh(byRef)) return byRef;
  return null;
}

export function clearExpiredPublicBookingConfirmationSnapshots() {
  const store = readStore();
  let changed = false;
  for (const key of Object.keys(store.byOrderId)) {
    if (!isFresh(store.byOrderId[key])) {
      delete store.byOrderId[key];
      changed = true;
    }
  }
  for (const key of Object.keys(store.byOrderRef)) {
    if (!isFresh(store.byOrderRef[key])) {
      delete store.byOrderRef[key];
      changed = true;
    }
  }
  if (changed) writeStore(store);
}
